import { useEffect, useState } from 'react'
import { Plus, Pencil, Trash2 } from 'lucide-react'
import { PageHeader } from '@/components/ui/PageHeader'
import { Toolbar } from '@/components/ui/Toolbar'
import { DataTable, type DataTableColumn } from '@/components/ui/DataTable'
import { Pagination } from '@/components/ui/Pagination'
import { StatusDot } from '@/components/ui/StatusDot'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { StatusToggle } from '@/components/ui/StatusToggle'
import { Modal } from '@/components/ui/Modal'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { useCrudList } from '@/hooks/useCrudList'
import { useStatusFilter } from '@/hooks/useStatusFilter'
import { useDataTableExport } from '@/hooks/useDataTableExport'
import {
  ServiceProvidersService,
  type ServiceProvider,
} from '@/services/modules/ServiceProvidersService'
import { toast } from '@/components/ui/Toast'
import { useAuthority } from '@/hooks/useAuthority'

/**
 * Catálogo simples de prestadores (Rede Própria, Medicar, Porto, MAG, PAP,
 * Auster). Modal por ser apenas 1 campo + status.
 *
 * É a base do cadastro de Serviços — sem prestadores cadastrados, o
 * multi-select de prestadores no Serviço fica vazio.
 */
export function SettingsServiceProvidersPage() {
  const [reload, setReload] = useState(0)
  const { can } = useAuthority()
  const canCreate = can('settings.service_providers.create')
  const canEdit = can('settings.service_providers.edit')
  const canDelete = can('settings.service_providers.delete')
  const sf = useStatusFilter({ searchPlaceholder: 'Buscar prestador' })
  const { setPage, size, setSize, rows, meta, loading } = useCrudList(
    ServiceProvidersService,
    { size: 25, reloadKey: reload, filters: sf.filters },
  )
  const [openForm, setOpenForm] = useState<{ open: boolean; item?: ServiceProvider }>({ open: false })
  const [openDelete, setOpenDelete] = useState<ServiceProvider | null>(null)

  const exp = useDataTableExport({
    filename: 'prestadores-servico',
    service: ServiceProvidersService,
    filters: sf.filters,
    columns: [
      { header: 'Nome',   accessor: (p: ServiceProvider) => p.name },
      { header: 'Status', accessor: (p: ServiceProvider) => p.status },
    ],
  })

  async function handleSave(payload: Partial<ServiceProvider>) {
    try {
      if (openForm.item) {
        await ServiceProvidersService.update(openForm.item.id, payload)
        toast.success({ title: 'Prestador atualizado', description: payload.name ?? '' })
      } else {
        await ServiceProvidersService.create(payload)
        toast.success({ title: 'Prestador criado', description: payload.name ?? '' })
      }
      setOpenForm({ open: false })
      setReload(v => v + 1)
    } catch (e) {
      const msg = (e as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'Erro ao salvar.'
      toast.error({ title: 'Não foi possível salvar', description: msg })
    }
  }
  async function handleDelete() {
    if (!openDelete) return
    try {
      await ServiceProvidersService.destroy(openDelete.id)
      toast.success({ title: 'Prestador removido', description: openDelete.name })
      setOpenDelete(null)
      setReload(v => v + 1)
    } catch (e) {
      const msg = (e as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'Erro ao remover.'
      toast.error({ title: 'Não foi possível remover', description: msg })
    }
  }

  const columns: DataTableColumn<ServiceProvider>[] = [
    {
      key: 'name',
      header: 'Prestador',
      sortable: true,
      sortKey: 'name',
      alwaysVisible: true,
      render: p => <span className="font-medium text-gray-800">{p.name}</span>,
    },
    {
      key: 'status',
      header: 'Status',
      align: 'center',
      className: 'w-24',
      render: p => <StatusDot value={p.status} />,
    },
  ]

  return (
    <>
      <PageHeader
        title="Prestadores de serviço"
        subtitle="Quem executa os serviços (Rede Própria, Medicar, Porto Seguro, MAG)"
        crumbs={[{ label: 'Configurações', to: '/settings' }, { label: 'Comercial' }, { label: 'Prestadores' }]}
        actions={canCreate ? <Button onClick={() => setOpenForm({ open: true })} iconLeft={<Plus size={14} />}>Novo prestador</Button> : undefined}
      />
      <Toolbar onFiltersClick={sf.open} activeFiltersCount={sf.activeCount} />
      <DataTable
        tableId="settings.commercial.service-providers"
        columns={columns}
        rows={rows}
        loading={loading}
        getRowKey={p => p.id}
        exportable
        onExportPage={() => exp.exportPage(rows)}
        onExportAll={exp.exportAll}
        rowActions={(p) => [
          ...(canEdit ? [{ key: 'edit', label: 'Editar', icon: <Pencil size={14} />, onClick: () => setOpenForm({ open: true, item: p }) }] : []),
          ...(canDelete ? [{ key: 'delete', label: 'Remover', icon: <Trash2 size={14} />, danger: true, divider: canEdit, onClick: () => setOpenDelete(p) }] : []),
        ]}
      />
      <Pagination meta={meta} onChange={setPage} size={size} onSizeChange={setSize} />

      <ServiceProviderForm
        open={openForm.open}
        item={openForm.item}
        onClose={() => setOpenForm({ open: false })}
        onSave={handleSave}
      />
      <ConfirmDialog
        open={!!openDelete}
        onClose={() => setOpenDelete(null)}
        onConfirm={handleDelete}
        title="Remover prestador?"
        description={`Tem certeza que deseja remover "${openDelete?.name}"? Esta ação não pode ser desfeita.`}
        confirmLabel="Remover"
        variant="danger"
      />
      {sf.drawer}
    </>
  )
}

function ServiceProviderForm({ open, item, onClose, onSave }: {
  open: boolean
  item?: ServiceProvider
  onClose: () => void
  onSave: (payload: Partial<ServiceProvider>) => Promise<void>
}) {
  const [name, setName] = useState('')
  const [status, setStatus] = useState<'ATIVO' | 'INATIVO'>('ATIVO')
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!open) return
    setName(item?.name ?? '')
    setStatus(item?.status ?? 'ATIVO')
  }, [open, item])

  async function submit() {
    setSaving(true)
    try {
      await onSave({ name, status } as Partial<ServiceProvider>)
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={item ? 'Editar prestador' : 'Novo prestador'}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Cancelar</Button>
          <Button onClick={submit} loading={saving} disabled={!name.trim()}>Salvar</Button>
        </>
      }
    >
      <div className="grid grid-cols-1 gap-3">
        <Input
          label="Nome"
          required
          value={name}
          onChange={e => setName(e.target.value)}
          placeholder="Porto Seguro"
        />
        <StatusToggle value={status} onChange={setStatus} />
      </div>
    </Modal>
  )
}
